/**
 * One transaction as a TABLE ROW: a read row with an inline category dropdown, and
 * — when you hit Edit — a second full-width row holding the edit form.
 *
 * Re-categorizing here is the learning loop — the Worker upserts a category rule
 * from the merchant name, so the next one of these fills itself in.
 */
import { useId, useState } from 'react';
import type { BudgetCategory, Transaction } from '../../shared/types';
import { api, money, parseMoney } from '../../lib/api';
import { dayLabel } from './dates';
import { buttonClass, inputClass, labelClass, quietButtonClass } from './ui';

/** Columns in the table — the edit row spans all of them. Keep in sync with the header. */
export const TX_COLUMNS = 6;

interface Props {
  transaction: Transaction;
  categories: BudgetCategory[];
  onChanged: (t: Transaction) => void;
  onDeleted: (t: Transaction) => void;
  onFlash: (message: string) => void;
  onError: (message: string) => void;
}

export default function TransactionRow({
  transaction: t,
  categories,
  onChanged,
  onDeleted,
  onFlash,
  onError,
}: Props) {
  const ids = useId();
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const [date, setDate] = useState(t.occurred_on);
  const [description, setDescription] = useState(t.description);
  const [amount, setAmount] = useState((t.amount / 100).toFixed(2));
  const [account, setAccount] = useState(t.source_account ?? '');
  const [notes, setNotes] = useState(t.notes ?? '');

  async function recategorize(value: string) {
    const next = value === '' ? null : Number(value);
    if (next === t.category_id) return;

    setBusy(true);
    try {
      const saved = await api.put<Transaction>(`/transactions/${t.id}`, { category_id: next });
      onChanged(saved);
      const name = categories.find((c) => c.id === next)?.name;
      onFlash(
        next === null
          ? `${t.description} moved to Uncategorized`
          : `Learned — the next “${t.description}” will fill in as ${name}`
      );
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Could not change the category.');
    } finally {
      setBusy(false);
    }
  }

  async function saveEdit(e: React.FormEvent) {
    e.preventDefault();
    const cents = parseMoney(amount);
    if (!description.trim()) {
      setProblem('Description is required.');
      return;
    }
    if (cents === null || cents === 0) {
      setProblem('Amount must be a number, like 24.99.');
      return;
    }

    setBusy(true);
    setProblem(null);
    try {
      const saved = await api.put<Transaction>(`/transactions/${t.id}`, {
        occurred_on: date,
        description: description.trim(),
        amount: cents,
        source_account: account.trim() || null,
        notes: notes.trim() || null,
      });
      onChanged(saved);
      setEditing(false);
      onFlash('Saved');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not save that.';
      setProblem(message);
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    // Not an erasure — the Worker voids the row (active = 0). It leaves the list and
    // stops counting toward the budget, and it stays in D1 if it ever has to come back.
    if (
      !window.confirm(
        `Remove “${t.description}” for ${money(t.amount)}? It stops counting toward the budget.`
      )
    ) {
      return;
    }
    setBusy(true);
    try {
      await api.del<Transaction>(`/transactions/${t.id}`);
      onDeleted(t);
      onFlash('Removed from the budget');
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Could not delete that.');
      setBusy(false);
    }
  }

  const cell = 'px-3 py-2.5 align-top';

  return (
    <>
      <tr
        className={`border-b border-border transition-opacity ${busy ? 'opacity-60' : ''} ${
          t.category_id === null ? 'bg-honey-soft/30' : ''
        } ${editing ? 'border-b-0' : ''}`}
      >
        <td className={`${cell} whitespace-nowrap text-sm text-ink-soft`}>{dayLabel(t.occurred_on)}</td>

        <td className={cell}>
          <p className="font-medium text-ink">{t.description}</p>
          {t.notes && <p className="mt-0.5 text-xs text-ink-faint">{t.notes}</p>}
        </td>

        <td className={cell}>
          <label className="sr-only" htmlFor={`${ids}-cat`}>
            Category for {t.description}
          </label>
          <select
            id={`${ids}-cat`}
            value={t.category_id ?? ''}
            disabled={busy}
            onChange={(e) => recategorize(e.target.value)}
            className={`min-h-[40px] w-full max-w-[11rem] rounded-lg border bg-surface px-2 py-1.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-sage/40 ${
              t.category_id === null
                ? 'border-honey bg-honey-soft font-medium text-ink'
                : 'border-border text-ink'
            }`}
          >
            <option value="">{t.category_id === null ? 'Needs a category' : 'Uncategorized'}</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </td>

        <td className={`${cell} hidden text-sm text-ink-soft md:table-cell`}>
          {t.source_account ?? <span className="text-ink-faint">—</span>}
        </td>

        <td
          className={`${cell} whitespace-nowrap text-right font-medium tabular-nums ${
            t.amount < 0 ? 'text-sage' : 'text-ink'
          }`}
        >
          {t.amount < 0 ? `+${money(Math.abs(t.amount))}` : money(t.amount)}
        </td>

        <td className={`${cell} text-right`}>
          <button
            type="button"
            onClick={() => setEditing((v) => !v)}
            aria-expanded={editing}
            className="min-h-[40px] rounded-lg px-3 text-sm font-medium text-ink-soft underline underline-offset-2 hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-sage/50"
          >
            {editing ? 'Close' : 'Edit'}
            <span className="sr-only"> {t.description}</span>
          </button>
        </td>
      </tr>

      {editing && (
        <tr className="border-b border-border bg-surface-sunk/40">
          <td colSpan={TX_COLUMNS} className="px-3 pb-4 pt-1">
            <form onSubmit={saveEdit} className="grid grid-cols-1 gap-3 sm:grid-cols-12">
              <div className="sm:col-span-3">
                <label className={labelClass} htmlFor={`${ids}-date`}>
                  Date
                </label>
                <input
                  id={`${ids}-date`}
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className={inputClass}
                  required
                />
              </div>
              <div className="sm:col-span-6">
                <label className={labelClass} htmlFor={`${ids}-desc`}>
                  Merchant / description
                </label>
                <input
                  id={`${ids}-desc`}
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className={inputClass}
                />
              </div>
              <div className="sm:col-span-3">
                <label className={labelClass} htmlFor={`${ids}-amount`}>
                  Amount
                </label>
                <input
                  id={`${ids}-amount`}
                  type="text"
                  inputMode="decimal"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className={inputClass}
                />
              </div>
              <div className="sm:col-span-4">
                <label className={labelClass} htmlFor={`${ids}-account`}>
                  Account
                </label>
                <input
                  id={`${ids}-account`}
                  type="text"
                  value={account}
                  onChange={(e) => setAccount(e.target.value)}
                  className={inputClass}
                />
              </div>
              <div className="sm:col-span-8">
                <label className={labelClass} htmlFor={`${ids}-notes`}>
                  Notes
                </label>
                <input
                  id={`${ids}-notes`}
                  type="text"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Anything worth remembering"
                  className={inputClass}
                />
              </div>

              <div className="flex flex-wrap gap-2 sm:col-span-12">
                <button type="submit" disabled={busy} className={buttonClass}>
                  {busy ? 'Saving…' : 'Save'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setEditing(false);
                    setProblem(null);
                    setDate(t.occurred_on);
                    setDescription(t.description);
                    setAmount((t.amount / 100).toFixed(2));
                    setAccount(t.source_account ?? '');
                    setNotes(t.notes ?? '');
                  }}
                  className={quietButtonClass}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={remove}
                  disabled={busy}
                  className={`${quietButtonClass} ml-auto text-over hover:text-over`}
                >
                  Delete
                </button>
              </div>

              {problem && (
                <p aria-live="polite" className="text-sm text-over sm:col-span-12">
                  <span aria-hidden="true">⚠ </span>
                  {problem}
                </p>
              )}
            </form>
          </td>
        </tr>
      )}
    </>
  );
}
